import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Check } from 'lucide-react';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
  disabled?: boolean;
  className?: string;
}

export const PullToRefresh = ({
  children,
  onRefresh,
  disabled = false,
  className = '',
}: PullToRefreshProps) => {
  const {
    isPulling,
    isRefreshing,
    pullDistance,
    canRefresh,
    progress,
  } = usePullToRefresh({
    onRefresh,
    disabled,
  });

  const showIndicator = isPulling || isRefreshing;
  const indicatorHeight = Math.min(pullDistance, 80);

  return (
    <div className={`relative ${className}`}>
      {/* Pull indicator */}
      <AnimatePresence>
        {showIndicator && (
          <motion.div
            className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center overflow-hidden bg-gradient-to-b from-primary/10 to-transparent"
            initial={{ height: 0, opacity: 0 }}
            animate={{ 
              height: isRefreshing ? 60 : indicatorHeight,
              opacity: 1 
            }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            style={{ 
              paddingTop: 'env(safe-area-inset-top, 0)',
            }}
          >
            <motion.div
              className={`flex items-center justify-center rounded-full p-2 ${
                canRefresh || isRefreshing 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground'
              }`}
              animate={{
                scale: canRefresh ? 1.1 : 1,
                rotate: isRefreshing ? 360 : progress * 180,
              }}
              transition={{
                rotate: isRefreshing 
                  ? { duration: 1, repeat: Infinity, ease: 'linear' }
                  : { duration: 0.1 },
                scale: { type: 'spring', stiffness: 400, damping: 20 },
              }}
            >
              {isRefreshing ? (
                <RefreshCw className="h-5 w-5" />
              ) : canRefresh ? (
                <Check className="h-5 w-5" />
              ) : (
                <motion.div
                  style={{ rotate: progress * 180 }}
                >
                  <RefreshCw className="h-5 w-5" />
                </motion.div>
              )}
            </motion.div>

            {/* Progress arc */}
            {!isRefreshing && isPulling && (
              <svg 
                className="absolute" 
                width="44" 
                height="44" 
                viewBox="0 0 44 44"
                style={{ transform: 'rotate(-90deg)' }}
              >
                <circle
                  cx="22"
                  cy="22"
                  r="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray={`${progress * 113} 113`}
                  className={canRefresh ? 'text-primary' : 'text-muted-foreground/30'}
                />
              </svg>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content with pull transform */}
      <motion.div
        animate={{
          y: isRefreshing ? 60 : isPulling ? indicatorHeight * 0.5 : 0,
        }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      >
        {children}
      </motion.div>
    </div>
  );
};
